import React, { useState, useEffect, lazy, Suspense } from 'react';
import { X, Save, Trash2, FileCheck, Calendar, User, Building, Users, Link2, Check, XCircle, ThumbsUp, ThumbsDown, DollarSign, Loader2, AlertTriangle, Plus, Minus, CheckCircle, Clock, History, FileText, ListChecks, ChevronDown, ChevronUp, ExternalLink, FolderTree } from 'lucide-react';
import { api } from '../api/client';
import EntityPicker from './EntityPicker';
import WBSNodeSelector from './WBSNodeSelector';
import axios from 'axios';

// Lazy load FinancialImpactPreview to avoid circular deps
const FinancialImpactPreview = lazy(() => import('./FinancialImpactPreview'));

const STATUSES = ['awaiting_approval', 'approved', 'rejected', 'pending'];
const IMPACTS = ['low', 'medium', 'high', 'critical'];
const CATEGORIES = ['general', 'technical', 'resource', 'budget', 'scope', 'schedule'];
const ESCALATION_LEVELS = ['Project', 'Program', 'Portfolio', 'Executive', 'SteerCo'];

// API helpers for financial impact
const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const checkFinancialBaseline = async (scopeId) => {
  try {
    const res = await axios.get(`${API_BASE}/financial/baseline/current?scopeId=${scopeId}`, { headers: getAuthHeaders() });
    return res.data.baseline;
  } catch {
    return null;
  }
};

// Options Considered Section Component
const OptionsConsideredSection = ({ options, selectedOption }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!options || options.length === 0) return null;
  
  return (
    <div className="border rounded-xl overflow-hidden" data-testid="options-considered-section">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100"
      >
        <span className="flex items-center gap-2 font-medium text-blue-800">
          <ListChecks className="w-5 h-5" />
          Options Considered ({options.length})
        </span>
        {expanded ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-blue-600" />}
      </button>
      
      {expanded && (
        <div className="p-4 space-y-3">
          {options.map((option, idx) => (
            <div 
              key={option.option_id || idx} 
              className={`border rounded-lg p-3 ${option.selected || option.option_id === selectedOption ? 'border-green-400 bg-green-50' : 'border-gray-200'}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    {(option.selected || option.option_id === selectedOption) && (
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    )}
                    <span className="font-medium text-gray-900">{option.title}</span>
                  </div>
                  {option.description && (
                    <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                  )}
                </div>
                <span className={`text-xs px-2 py-1 rounded ${
                  option.risk_level === 'low' ? 'bg-green-100 text-green-700' :
                  option.risk_level === 'medium' ? 'bg-amber-100 text-amber-700' :
                  option.risk_level === 'high' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {option.risk_level || 'medium'} risk
                </span>
              </div>
              
              {(option.pros?.length > 0 || option.cons?.length > 0) && (
                <div className="grid grid-cols-2 gap-3 mt-2 pt-2 border-t">
                  {option.pros?.length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-green-700 uppercase">Pros</span>
                      <ul className="mt-1 space-y-1">
                        {option.pros.map((pro, i) => (
                          <li key={i} className="text-xs text-green-600 flex items-start gap-1">
                            <Plus className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            {pro}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {option.cons?.length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-red-700 uppercase">Cons</span>
                      <ul className="mt-1 space-y-1">
                        {option.cons.map((con, i) => (
                          <li key={i} className="text-xs text-red-600 flex items-start gap-1">
                            <Minus className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            {con}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              {option.cost_estimate && (
                <div className="mt-2 pt-2 border-t text-xs text-gray-600">
                  <DollarSign className="w-3 h-3 inline mr-1" />
                  Estimated Cost: ${option.cost_estimate.toLocaleString()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Linked Artifacts Section Component
const LinkedArtifactsSection = ({ artifacts }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!artifacts || artifacts.length === 0) return null;
  
  const getArtifactIcon = (type) => {
    switch (type) {
      case 'requirement': return FileText;
      case 'epic': return FileCheck;
      case 'task': return ListChecks;
      case 'scope': return Building;
      case 'risk': return AlertTriangle;
      default: return Link2;
    }
  };
  
  const getLinkTypeColor = (linkType) => {
    switch (linkType) {
      case 'impacted': return 'bg-amber-100 text-amber-700';
      case 'blocked_by': return 'bg-red-100 text-red-700';
      case 'enables': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };
  
  return (
    <div className="border rounded-xl overflow-hidden" data-testid="linked-artifacts-section">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-purple-50 hover:bg-purple-100"
      >
        <span className="flex items-center gap-2 font-medium text-purple-800">
          <Link2 className="w-5 h-5" />
          Linked Artifacts ({artifacts.length})
        </span>
        {expanded ? <ChevronUp className="w-5 h-5 text-purple-600" /> : <ChevronDown className="w-5 h-5 text-purple-600" />}
      </button>
      
      {expanded && (
        <div className="p-4 space-y-2">
          {artifacts.map((artifact, idx) => {
            const Icon = getArtifactIcon(artifact.artifact_type);
            return (
              <div 
                key={artifact.artifact_id || idx} 
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-4 h-4 text-gray-500" />
                  <div>
                    <span className="text-sm font-medium text-gray-900">
                      {artifact.artifact_title || artifact.artifact_id}
                    </span>
                    <span className="text-xs text-gray-500 ml-2 capitalize">
                      {artifact.artifact_type}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${getLinkTypeColor(artifact.link_type)}`}>
                    {artifact.link_type?.replace('_', ' ') || 'related'}
                  </span>
                  <ExternalLink className="w-4 h-4 text-gray-400 hover:text-blue-600 cursor-pointer" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Audit History Section Component
const AuditHistorySection = ({ history }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!history || history.length === 0) return null;
  
  const sortedHistory = [...history].sort((a, b) => 
    new Date(b.timestamp) - new Date(a.timestamp)
  );
  
  const getActionIcon = (action) => {
    if (action?.includes('approve')) return CheckCircle;
    if (action?.includes('reject')) return XCircle;
    if (action?.includes('create')) return Plus;
    if (action?.includes('update')) return FileCheck;
    return Clock;
  };
  
  const getActionColor = (action) => {
    if (action?.includes('approve')) return 'text-green-600 bg-green-50';
    if (action?.includes('reject')) return 'text-red-600 bg-red-50';
    if (action?.includes('create')) return 'text-blue-600 bg-blue-50';
    return 'text-gray-600 bg-gray-50';
  };
  
  return (
    <div className="border rounded-xl overflow-hidden" data-testid="audit-history-section">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-gray-100 hover:bg-gray-200"
      >
        <span className="flex items-center gap-2 font-medium text-gray-700">
          <History className="w-5 h-5" />
          Audit History ({history.length})
        </span>
        {expanded ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
      </button>
      
      {expanded && (
        <div className="p-4">
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
            
            <div className="space-y-4">
              {sortedHistory.slice(0, 10).map((entry, idx) => {
                const Icon = getActionIcon(entry.action);
                const colorClass = getActionColor(entry.action);
                
                return (
                  <div key={idx} className="relative pl-10">
                    <div className={`absolute left-2 w-5 h-5 rounded-full flex items-center justify-center ${colorClass}`}>
                      <Icon className="w-3 h-3" />
                    </div>
                    
                    <div className="bg-white border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-900 capitalize">
                          {entry.action?.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-gray-500">
                          {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ''}
                        </span>
                      </div>
                      
                      {entry.user_name && (
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {entry.user_name}
                        </div>
                      )}
                      
                      {entry.role && (
                        <div className="text-xs text-gray-500 mt-1">
                          Role: <span className="font-medium">{entry.role}</span>
                        </div>
                      )}
                      
                      {entry.notes && (
                        <p className="text-xs text-gray-600 mt-2 italic">"{entry.notes}"</p>
                      )}
                      
                      {entry.changed_fields && entry.changed_fields.length > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          Changed: {entry.changed_fields.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {sortedHistory.length > 10 && (
                <div className="text-center text-xs text-gray-500 pt-2">
                  +{sortedHistory.length - 10} more entries
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Decision Create/Edit Drawer
 */
const DecisionDrawer = ({ 
  isOpen, 
  onClose, 
  decision = null, 
  defaultProjectId = null,
  defaultTeamId = null,
  onSuccess 
}) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'general',
    status: 'awaiting_approval',
    request_date: new Date().toISOString().split('T')[0],
    due_date: '',
    decision_date: '',
    requestor: '',
    approver: '',
    outcome: '',
    impact: 'medium',
    escalation_level: 'Project',
    team_id: defaultTeamId || '',
    project_id: defaultProjectId || '',
    wbs_id: null,  // WBS Node reference
    links: []
  });
  
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showFinancialImpact, setShowFinancialImpact] = useState(false);
  const [hasFinancialBaseline, setHasFinancialBaseline] = useState(false);
  const [financialApprovalPending, setFinancialApprovalPending] = useState(false);

  // Load form data when editing
  useEffect(() => {
    if (decision) {
      setForm({
        title: decision.title || decision.decision || '',
        description: decision.description || '',
        category: decision.category?.toLowerCase() || 'general',
        status: decision.status?.toLowerCase().replace(' ', '_') || 'awaiting_approval',
        request_date: decision.request_date?.split('T')[0] || '',
        due_date: decision.due_date?.split('T')[0] || '',
        decision_date: decision.decision_date?.split('T')[0] || '',
        requestor: decision.requestor || '',
        approver: decision.approver || '',
        outcome: decision.outcome || '',
        impact: decision.impact?.toLowerCase() || 'medium',
        escalation_level: decision.escalation_level || 'Project',
        team_id: decision.team_id || '',
        project_id: decision.project_id || '',
        wbs_id: decision.wbs_id || null,  // WBS Node reference
        links: decision.links || []
      });
    } else {
      setForm({
        title: '',
        description: '',
        category: 'general',
        status: 'awaiting_approval',
        request_date: new Date().toISOString().split('T')[0],
        due_date: '',
        decision_date: '',
        requestor: '',
        approver: '',
        outcome: '',
        impact: 'medium',
        escalation_level: 'Project',
        team_id: defaultTeamId || '',
        project_id: defaultProjectId || '',
        wbs_id: null,  // WBS Node reference
        links: []
      });
    }
  }, [decision, defaultProjectId, defaultTeamId]);

  // Load projects and teams
  useEffect(() => {
    const loadData = async () => {
      try {
        const [projectsRes, teamsRes] = await Promise.all([
          api.getProjects(),
          api.getTeams()
        ]);
        setProjects(projectsRes.data?.projects || []);
        setTeams(teamsRes.data?.teams || []);
      } catch (err) {
        console.error('Failed to load dropdown data:', err);
      }
    };
    if (isOpen) loadData();
  }, [isOpen]);

  // Check for financial baseline when decision has a scope
  useEffect(() => {
    const checkBaseline = async () => {
      const scopeId = decision?.scope_id || decision?.scopeId;
      if (scopeId && decision?.id) {
        const baseline = await checkFinancialBaseline(scopeId);
        setHasFinancialBaseline(!!baseline);
        // Auto-show financial impact for budget/scope decisions
        if (baseline && ['budget', 'scope'].includes(form.category)) {
          setShowFinancialImpact(true);
        }
      } else {
        setHasFinancialBaseline(false);
      }
    };
    if (isOpen && decision) {
      checkBaseline();
    }
  }, [isOpen, decision, form.category]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (decision?.id) {
        await api.updateDecision(decision.id, form);
      } else {
        await api.createDecisionNew(form);
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!decision?.id) return;
    if (!window.confirm('Are you sure you want to delete this decision?')) return;

    setDeleting(true);
    try {
      await api.deleteDecision(decision.id);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleApprove = async () => {
    if (!decision?.id) return;
    setSaving(true);
    setFinancialApprovalPending(true);
    try {
      // Approve the decision
      await api.approveDecisionNew(decision.id, { notes: form.outcome });
      
      // If there's a financial baseline, commit the financial impact
      if (hasFinancialBaseline) {
        try {
          await axios.post(
            `${API_BASE}/financial/impact/commit?decisionId=${decision.id}`,
            {},
            { headers: getAuthHeaders() }
          );
          console.log('Financial baseline vNext created');
        } catch (finErr) {
          console.warn('Financial impact commit failed:', finErr);
          // Don't fail the approval if financial commit fails
        }
      }
      
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setSaving(false);
      setFinancialApprovalPending(false);
    }
  };

  const handleReject = async () => {
    if (!decision?.id) return;
    setSaving(true);
    try {
      await api.rejectDecisionNew(decision.id, { notes: form.outcome });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const title = decision?.id ? 'Edit Decision' : 'New Decision';
  const isPending = form.status === 'awaiting_approval' || form.status === 'pending';

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      
      {/* Drawer */}
      <div className="relative w-full max-w-xl bg-white shadow-2xl h-full overflow-y-auto animate-slide-in-right">
        {/* Header */}
        <div className="sticky top-0 z-10 px-6 py-4 border-b border-[var(--clr-border-light)] flex items-center justify-between bg-blue-50">
          <div className="flex items-center gap-3">
            <FileCheck className="w-6 h-6 text-blue-600" />
            <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">{title}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/10 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Quick Actions for existing pending decisions */}
          {decision?.id && isPending && (
            <div className="flex gap-2 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <button
                type="button"
                onClick={handleApprove}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                {financialApprovalPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4" />}
                {hasFinancialBaseline ? 'Approve & Create Baseline' : 'Approve'}
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <ThumbsDown className="w-4 h-4" />
                Reject
              </button>
            </div>
          )}

          {/* Financial Impact Preview - Shows for decisions with financial baselines */}
          {decision?.id && hasFinancialBaseline && (
            <div className="border rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setShowFinancialImpact(!showFinancialImpact)}
                className="w-full flex items-center justify-between p-4 bg-green-50 hover:bg-green-100"
                data-testid="toggle-financial-impact-btn"
              >
                <span className="flex items-center gap-2 font-medium text-green-800">
                  <DollarSign className="w-5 h-5" />
                  Financial Impact Analysis
                </span>
                <span className="text-sm text-green-600">
                  {showFinancialImpact ? 'Hide' : 'Show'} Impact Preview
                </span>
              </button>
              {showFinancialImpact && (
                <Suspense fallback={
                  <div className="p-6 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                }>
                  <div className="p-4">
                    <FinancialImpactPreview 
                      decisionId={decision.id} 
                      showCommitButton={false}
                    />
                  </div>
                </Suspense>
              )}
            </div>
          )}

          {/* Schedule Drift Warning */}
          {decision?.id && hasFinancialBaseline && isPending && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <strong>Schedule Drift Warning:</strong> Approving this decision will update the delivery forecast. 
                Review financial impact before approval. Dates will not auto-adjust.
              </div>
            </div>
          )}

          {/* Options Considered Section - Enhanced Sprint 14 */}
          {decision?.id && decision?.options_considered && decision.options_considered.length > 0 && (
            <OptionsConsideredSection options={decision.options_considered} selectedOption={decision.selected_option} />
          )}

          {/* Linked Artifacts Section - Enhanced Sprint 14 */}
          {decision?.id && decision?.linked_artifacts && decision.linked_artifacts.length > 0 && (
            <LinkedArtifactsSection artifacts={decision.linked_artifacts} />
          )}

          {/* Audit History Section - Enhanced Sprint 14 */}
          {decision?.id && decision?.history && decision.history.length > 0 && (
            <AuditHistorySection history={decision.history} />
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
              placeholder="Decision title"
              data-testid="decision-title-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
              placeholder="Describe the decision needed..."
            />
          </div>

          {/* Category & Status Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg capitalize"
              >
                {CATEGORIES.map(c => (
                  <option key={c} value={c} className="capitalize">{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg capitalize"
              >
                {STATUSES.map(s => (
                  <option key={s} value={s} className="capitalize">{s.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Impact & Escalation */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Impact</label>
              <select
                value={form.impact}
                onChange={(e) => setForm({ ...form, impact: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg capitalize"
              >
                {IMPACTS.map(i => (
                  <option key={i} value={i} className="capitalize">{i}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Escalation Level</label>
              <select
                value={form.escalation_level}
                onChange={(e) => setForm({ ...form, escalation_level: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
              >
                {ESCALATION_LEVELS.map(l => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dates Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Request Date
              </label>
              <input
                type="date"
                value={form.request_date}
                onChange={(e) => setForm({ ...form, request_date: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Due Date
              </label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
              />
            </div>
          </div>

          {/* Requestor & Approver */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
                <User className="w-4 h-4 inline mr-1" />
                Requestor
              </label>
              <input
                type="text"
                value={form.requestor}
                onChange={(e) => setForm({ ...form, requestor: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
                placeholder="Who is requesting?"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
                <User className="w-4 h-4 inline mr-1" />
                Approver
              </label>
              <input
                type="text"
                value={form.approver}
                onChange={(e) => setForm({ ...form, approver: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
                placeholder="Who will approve?"
              />
            </div>
          </div>

          {/* Project & Team */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
                <Building className="w-4 h-4 inline mr-1" />
                Project
              </label>
              <select
                value={form.project_id}
                onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
              >
                <option value="">Select Project</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
                <Users className="w-4 h-4 inline mr-1" />
                Team
              </label>
              <select
                value={form.team_id}
                onChange={(e) => setForm({ ...form, team_id: e.target.value })}
                className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
              >
                <option value="">Select Team</option>
                {teams.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* WBS Node Selector */}
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
              <FolderTree className="w-4 h-4 inline mr-1" />
              WBS Location
            </label>
            <WBSNodeSelector
              value={form.wbs_id}
              onChange={(wbsId) => setForm({ ...form, wbs_id: wbsId })}
              placeholder="Select WBS node"
              allowClear={true}
              data-testid="decision-wbs-selector"
            />
            <p className="text-xs text-gray-500 mt-1">
              Link this decision to a Work Breakdown Structure node
            </p>
          </div>

          {/* Linked Entities */}
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
              <Link2 className="w-4 h-4 inline mr-1" />
              Linked Entities
            </label>
            <EntityPicker
              multi={true}
              value={form.links}
              onChange={(links) => setForm({ ...form, links })}
              placeholder="Link to portfolios, programs, epics, vendors..."
            />
          </div>

          {/* Outcome (for approved/rejected) */}
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">Outcome / Notes</label>
            <textarea
              value={form.outcome}
              onChange={(e) => setForm({ ...form, outcome: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 border border-[var(--clr-border-light)] rounded-lg"
              placeholder="Decision outcome or notes..."
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-[var(--clr-border-light)]">
            {decision?.id && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
            <div className={`flex gap-2 ${!decision?.id ? 'ml-auto' : ''}`}>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-[var(--clr-text-secondary)] hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50"
                data-testid="save-decision-btn"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DecisionDrawer;
